import { Button, Checkbox, Input } from '@invent/wl-ui-kit-next/ui';
import * as React from 'react';

import { ChoiceSelect, HelpPopover } from '@/design';
import type { IEtagged } from '@/domains/api';
import type {
  ISegmentRule,
  ITerritoryEntry,
  ITerritoryVocabulary,
  SegmentMetric,
  TerritorySegment
} from '../../api/ranking-process-contracts';
import { rankingProcessApi } from '../../api/ranking-process-api';
import { LoadError, useAdminData } from '../../components/admin-page';
import { refusalMessage } from '../../refusal';
import { TerritoryMapping } from './territory-mapping';
import { WholeSaveFooter, wholeSaveRefusal, type WholeSaid } from './whole-save';

/**
 * A territorial list's territory vocabulary (SR-231, RNK-11; survey-api's territories design §3),
 * on the ranking's List setup tab: the territories the list ranks in, in order, each with the
 * rules that place an office in it — a two-letter state and, optionally, a city or the start of a
 * zip / postal code (SR-319).
 *
 * **Edited and saved whole** under `If-Match`, as survey-api replaces it: a territory removed from
 * the list here is retired there (an override may still name it), and one ticked "Retired" stays
 * listed but derives nothing. Nothing is judged here — a blank or repeated name, a state that is
 * not two letters, an office two territories claim are survey-api's 400, said by position; a stale
 * save is refused with Reload, which drops the draft. With no vocabulary yet (version 0), "Start from
 * published names" fills it with the names the list's published editions hold, no rule each.
 *
 * **Bounded, and found by name** (the stand-up list of 16 Oct: no endless vertical list in the
 * rankings setup): a Best-in-State list runs to some sixty territories, so each is one compact
 * row with its rules beside it, the rows scroll inside the card, and "Find a territory" narrows
 * them by name or state — the draft and every row's number stay whole under it. The rows found
 * are taken as the search is typed, so a row edited under it stays drawn; a move, a removal or a
 * new territory lets the search go. The search sits outside the fieldset: a reader finds too.
 *
 * **Segments** (SR-319): a territory may be marked HNW or PW, and the list's PW threshold — the
 * UHNW share of AUM from which a participant is PW — is saved with the territories; blank, the
 * list has no segments. survey-api derives the segment and judges the pairs. The whole vocabulary
 * also travels as a mapping file (`territory-mapping.tsx`), uploaded into this draft.
 *
 * Every reader sees it; saving is `rankings.manage`'s, drawn disabled with the reason. The live
 * names are also what Insert and Move suggest (`useTerritoryNames`), free text still allowed.
 */

/** A segment choice's values: '' for none. */
const SEGMENT_CHOICES = [
  { value: '', label: 'No segment' },
  { value: 'HNW', label: 'HNW' },
  { value: 'PW', label: 'PW' }
];

/** The draft as it compares with the saved vocabulary: a segment absent and null are the same. */
function comparable(entries: ITerritoryEntry[]): string {
  return JSON.stringify(entries.map((entry) => ({ ...entry, segment: entry.segment ?? null })));
}

/** The threshold box's text as sent: blank is null, anything else its number (NaN unsendable). */
function thresholdOf(text: string): Nullable<number> {
  return text.trim() === '' ? null : Number(text.trim());
}

/** The metrics a segment rule reads, as the screen names them. */
const METRIC_CHOICES = [
  { value: 'uhnw-aum', label: 'AUM (client segments)' },
  { value: 'uhnw-revenue', label: 'revenue (migrated survey)' }
];

/** "None" for no fallback: the kit's select takes a string. */
const NO_FALLBACK = 'none';

/** A list with no rule read (an older API): no split, AUM first, revenue after. */
const NO_RULE: ISegmentRule = { pwMinUhnwShare: null, metric: 'uhnw-aum', fallback: 'uhnw-revenue' };

/** The list's live territory names, for a territory box to suggest — none for a national list,
 *  a list with no vocabulary, or a read that failed. */
export function useTerritoryNames(
  surveyCode: string,
  list: string,
  territorial: boolean
): string[] {
  const { data } = useAdminData<string[]>(
    async (signal) => {
      if (!territorial) return [];
      const read = await rankingProcessApi.vocabulary(surveyCode, list, signal).catch(() => null);
      return (
        read?.body.territories.filter((entry) => !entry.retired).map((entry) => entry.name) ?? []
      );
    },
    [territorial]
  );
  return data ?? [];
}

export function VocabularyEditor({
  surveyCode,
  list,
  permitted
}: {
  surveyCode: string;
  list: string;
  permitted: boolean;
}) {
  const { data, error, reload, adopt } = useAdminData<Nullable<IEtagged<ITerritoryVocabulary>>>(
    async (signal) => {
      try {
        return await rankingProcessApi.vocabulary(surveyCode, list, signal);
      } catch (readError) {
        throw new Error(refusalMessage(readError, 'Could not read the territories'));
      }
    }
  );
  const [draft, setDraft] = React.useState<Nullable<ITerritoryEntry[]>>(null);
  const [threshold, setThreshold] = React.useState('');
  const [metric, setMetric] = React.useState<SegmentMetric>('uhnw-aum');
  const [fallback, setFallback] = React.useState<Nullable<SegmentMetric>>('uhnw-revenue');
  const metricId = React.useId();
  const fallbackId = React.useId();
  const resetRule = (rule: ISegmentRule) => {
    setThreshold(String(rule.pwMinUhnwShare ?? ''));
    setMetric(rule.metric);
    setFallback(rule.fallback);
  };
  const [drawn, setDrawn] = React.useState<Nullable<ITerritoryVocabulary>>(null);
  const [said, setSaid] = React.useState<Nullable<WholeSaid>>(null);
  const [busy, setBusy] = React.useState(false);
  const [find, setFind] = React.useState('');
  // The rows a search found, by draft index, taken as it is typed: a row edited under a search
  // stays drawn even once it no longer matches, rather than leaving from under the cursor.
  const [found, setFound] = React.useState<Nullable<ReadonlySet<number>>>(null);
  const rows = React.useRef<HTMLDivElement>(null);
  const vocabulary = data?.body ?? null;
  // A vocabulary read or saved replaces the draft — the draft is always of the version shown.
  if (vocabulary !== drawn) {
    setDrawn(vocabulary);
    setDraft(vocabulary && vocabulary.territories);
    resetRule(vocabulary?.segments ?? NO_RULE);
    // The rows a search found are indices into the draft just replaced.
    setFind('');
    setFound(null);
  }

  if (data === null) {
    return error ? (
      <LoadError message={error} />
    ) : (
      <p className="text-sm text-muted-foreground">Reading the territories…</p>
    );
  }
  if (vocabulary === null || draft === null) {
    return <p className="text-sm text-muted-foreground">A national list ranks in no territory.</p>;
  }

  const saved = vocabulary.segments ?? NO_RULE;
  const dirty =
    comparable(draft) !== comparable(vocabulary.territories) ||
    thresholdOf(threshold) !== saved.pwMinUhnwShare ||
    metric !== saved.metric ||
    fallback !== saved.fallback;
  const editable = permitted && !busy;
  const change = (index: number, entry: Partial<ITerritoryEntry>) =>
    setDraft(draft.map((current, at) => (at === index ? { ...current, ...entry } : current)));
  // A move or a removal renumbers the rows: the search is let go, as its found rows would be.
  const clearFind = () => {
    setFind('');
    setFound(null);
  };
  const move = (index: number, by: number) => {
    clearFind();
    const next = [...draft];
    const [moved] = next.splice(index, 1);
    next.splice(index + by, 0, moved);
    setDraft(next);
  };

  const save = async () => {
    if (data.etag === null) {
      setSaid({
        kind: 'refused',
        text: 'These territories came without their version — read them again.'
      });
      return;
    }
    const pwMinUhnwShare = thresholdOf(threshold);
    if (pwMinUhnwShare !== null && !Number.isFinite(pwMinUhnwShare)) {
      setSaid({ kind: 'refused', text: 'The PW threshold is not a number.' });
      return;
    }
    setBusy(true);
    setSaid(null);
    try {
      const answer = await rankingProcessApi.saveVocabulary(
        surveyCode,
        list,
        data.etag,
        draft,
        { pwMinUhnwShare, metric, fallback }
      );
      adopt(answer);
      setSaid({ kind: 'saved', text: `Saved — version ${answer.body.version}.` });
    } catch (failure) {
      setSaid(wholeSaveRefusal(failure, 'The territories were refused'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section
      aria-label="Territories of this list"
      className="grid max-w-5xl gap-4 rounded-md border border-border bg-white p-4"
    >
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <div className="flex items-center gap-1">
          <h2 className="text-base font-semibold text-[var(--color-royal-blue)]">
            Territories of this list
          </h2>
          <HelpPopover label="About the territories of this list">
            <p>
              An office is placed by the rule of its state with the longest start of its zip code,
              else by the rule naming its state and city, else by its state’s rule with neither. A
              territory removed here is retired: it derives nothing, and an override may still
              name it.
            </p>
            <p>
              With a PW threshold, a participant whose UHNW ($10MM+) share — of AUM from the
              survey’s client segments, or of revenue from the migrated survey — is at least the
              threshold is PW, anyone else HNW; where the first metric is not answered the second
              is read. Two territories of the same rules, one HNW and one PW, split an office
              between them by that segment.
            </p>
            <p>
              The thresholds are preliminary, confirm with Shook: 50% for FA Best-in-State and Top
              Women, 57% for Teams — the cut that best separated the 2026 editions’ HNW and PW
              territories by the migrated revenue share.
            </p>
            <p>
              The mapping file has one row per rule: Territory, Segment, State, City, Zip. An
              uploaded mapping is checked first and changes nothing until it is used and saved;
              the live territories it leaves out are retired on save.
            </p>
          </HelpPopover>
        </div>
        <span
          className="text-sm text-muted-foreground"
          data-vocabulary-version={vocabulary.version}
        >
          {vocabulary.version === 0 ? 'Not saved yet' : `Version ${vocabulary.version}`}
        </span>
      </div>
      <TerritoryMapping
        surveyCode={surveyCode}
        list={list}
        permitted={permitted}
        version={vocabulary.version}
        busy={busy}
        onUse={(territories) => {
          clearFind();
          setDraft(territories);
        }}
      />

      {vocabulary.version === 0 && draft.length === 0 && vocabulary.published.length > 0 && (
        <div>
          <Button
            type="button"
            size="sm"
            variant="outline"
            disabled={!editable}
            onClick={() =>
              setDraft(vocabulary.published.map((name) => ({ name, retired: false, rules: [] })))
            }
          >
            Start from published names
          </Button>
        </div>
      )}

      {draft.length > 0 && (
        // Outside the fieldset: a reader who cannot save still finds a territory.
        <div className="flex flex-wrap items-center gap-3">
          <Input
            type="search"
            aria-label="Find a territory"
            placeholder="Find a territory by name, state or zip…"
            className="w-80"
            value={find}
            onChange={(event) => {
              const needle = event.target.value;
              setFind(needle);
              setFound(
                needle.trim() === ''
                  ? null
                  : new Set(
                      draft.flatMap((entry, index) =>
                        matchesTerritory(entry, needle) ? [index] : []
                      )
                    )
              );
            }}
          />
          <span className="text-sm text-muted-foreground" data-territory-count>
            {found?.size ?? draft.length} of {draft.length} territories
          </span>
        </div>
      )}

      <fieldset className="grid gap-3" disabled={!editable}>
        <legend className="sr-only">Territories</legend>
        <div className="flex flex-wrap items-center gap-2 text-sm" data-segment-rule>
          <span id={metricId}>PW when UHNW share of</span>
          <ChoiceSelect
            labelledBy={metricId}
            className="w-56"
            value={metric}
            onChange={(next) => setMetric(next as SegmentMetric)}
            choices={METRIC_CHOICES}
          />
          ≥
          <Input
            inputMode="decimal"
            aria-label="PW threshold in percent"
            className="w-20"
            placeholder="off"
            value={threshold}
            onChange={(event) => setThreshold(event.target.value)}
          />
          %
          <span id={fallbackId} className="ml-2">
            if not answered,
          </span>
          <ChoiceSelect
            labelledBy={fallbackId}
            className="w-56"
            value={fallback ?? NO_FALLBACK}
            onChange={(next) => setFallback(next === NO_FALLBACK ? null : (next as SegmentMetric))}
            choices={[...METRIC_CHOICES, { value: NO_FALLBACK, label: 'nothing (HNW)' }]}
          />
        </div>
        {draft.length === 0 && <p className="text-sm text-muted-foreground">No territory yet.</p>}
        <div
          ref={rows}
          className="grid max-h-[calc(100dvh-22rem)] min-h-40 content-start gap-2 overflow-y-auto pr-1"
        >
          {draft.map((entry, index) => {
            const n = index + 1;
            if (found && !found.has(index)) return null;
            return (
              <div
                key={index}
                data-territory-entry={n}
                className="flex flex-wrap items-start gap-x-4 gap-y-2 rounded-md border border-border p-2"
              >
                <div className="flex flex-wrap items-center gap-2">
                  <Input
                    aria-label={`Territory ${n} name`}
                    className="w-64"
                    value={entry.name}
                    onChange={(event) => change(index, { name: event.target.value })}
                  />
                  <ChoiceSelect
                    aria-label={`Territory ${n} segment`}
                    className="w-32"
                    disabled={!editable}
                    value={entry.segment ?? ''}
                    onChange={(next) =>
                      change(index, { segment: next === '' ? null : (next as TerritorySegment) })
                    }
                    choices={SEGMENT_CHOICES}
                  />
                  <label className="flex items-center gap-2 text-sm">
                    <Checkbox
                      aria-label={`Territory ${n} retired`}
                      checked={entry.retired}
                      onCheckedChange={(next) => change(index, { retired: next === true })}
                    />
                    Retired
                  </label>
                  <Button
                    type="button"
                    size="sm"
                    variant="ghost"
                    aria-label={`Move territory ${n} up`}
                    disabled={index === 0}
                    onClick={() => move(index, -1)}
                  >
                    ↑
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    variant="ghost"
                    aria-label={`Move territory ${n} down`}
                    disabled={index === draft.length - 1}
                    onClick={() => move(index, 1)}
                  >
                    ↓
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    variant="ghost"
                    aria-label={`Remove territory ${n}`}
                    onClick={() => {
                      clearFind();
                      setDraft(draft.filter((_, at) => at !== index));
                    }}
                  >
                    Remove
                  </Button>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  {entry.rules.map((rule, ruleIndex) => {
                    const r = ruleIndex + 1;
                    const setRule = (next: Partial<typeof rule>) =>
                      change(index, {
                        rules: entry.rules.map((current, at) =>
                          at === ruleIndex ? { ...current, ...next } : current
                        )
                      });
                    return (
                      <div
                        key={ruleIndex}
                        className="flex items-center gap-1 rounded-md bg-[var(--accent)] p-1"
                      >
                        <Input
                          aria-label={`Territory ${n} rule ${r} state`}
                          className="w-20"
                          placeholder="CA"
                          value={rule.state}
                          onChange={(event) => setRule({ state: event.target.value })}
                        />
                        <Input
                          aria-label={`Territory ${n} rule ${r} city`}
                          className="w-40"
                          placeholder="any city"
                          value={rule.city ?? ''}
                          onChange={(event) =>
                            setRule({ city: event.target.value === '' ? null : event.target.value })
                          }
                        />
                        <Input
                          aria-label={`Territory ${n} rule ${r} zip`}
                          className="w-28"
                          placeholder="any zip"
                          value={rule.postalCode ?? ''}
                          onChange={(event) =>
                            setRule({
                              postalCode: event.target.value === '' ? null : event.target.value
                            })
                          }
                        />
                        <Button
                          type="button"
                          size="sm"
                          variant="ghost"
                          aria-label={`Remove territory ${n} rule ${r}`}
                          onClick={() =>
                            change(index, { rules: entry.rules.filter((_, at) => at !== ruleIndex) })
                          }
                        >
                          ✕
                        </Button>
                      </div>
                    );
                  })}
                  <div>
                    <Button
                      type="button"
                      size="sm"
                      variant="ghost"
                      aria-label={`Add a rule to territory ${n}`}
                      onClick={() =>
                        change(index, {
                          rules: [...entry.rules, { state: '', city: null, postalCode: null }]
                        })
                      }
                    >
                      Add a rule
                    </Button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
        <div>
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={() => {
              clearFind();
              setDraft([...draft, { name: '', retired: false, rules: [] }]);
              // The new row is the last: bring it into view once drawn.
              requestAnimationFrame(() =>
                rows.current?.scrollTo({ top: rows.current.scrollHeight })
              );
            }}
          >
            Add a territory
          </Button>
        </div>
      </fieldset>

      <WholeSaveFooter
        what="these territories"
        it="them"
        saveLabel="Save the territories"
        notPermitted="changing a list’s territories"
        said={said}
        busy={busy}
        editable={editable}
        dirty={dirty}
        permitted={permitted}
        onSave={() => void save()}
        onDiscard={() => {
          clearFind();
          setDraft(vocabulary.territories);
          resetRule(saved);
        }}
        onReload={() => void reload().then((read) => read && setSaid(null))}
      />
      {error && (
        <LoadError message={`Could not refresh — showing the territories as last read. ${error}`} />
      )}
    </section>
  );
}

/** Whether a territory answers "Find a territory": its name holds the text, a rule's state is
 *  it, or a rule's zip starts with it. */
function matchesTerritory(entry: ITerritoryEntry, find: string): boolean {
  const needle = find.trim().toLowerCase();
  return (
    entry.name.toLowerCase().includes(needle) ||
    entry.rules.some(
      (rule) =>
        rule.state.toLowerCase() === needle ||
        (rule.postalCode?.toLowerCase().startsWith(needle) ?? false)
    )
  );
}
